import { Badge, Button } from '@didto/ui';
import { kdsTabsFor } from '@didto/domain';
import type { KdsTab, PermissionKey } from '@didto/domain';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { closeTerminalSession, fetchRemoteApprovals } from '@/lib/api';
import { useDeviceStore } from '@/lib/deviceStore';
import { useSyncStatus } from '@/lib/network';
import { useReviewCounts } from '@/lib/review';
import { displayBusinessName } from '@/lib/businessName';
import { ConnectionBanner, SyncPill } from './connection';
import { depthForPath } from './depth';
import { useIdleLock } from './idle';
import { ShellBarContext } from './shellBar';
import { NEXT_THEME, THEME_LABEL, setThemePreference, useThemePreference } from './theme';
import { useSession } from './session';
import { useToast } from './toast';
import './shell.css';

interface NavItem {
  to: string;
  label: string;
  permission?: PermissionKey;
  humanOnly?: boolean;
  later?: boolean;
  /** A Kitchen / Bar tab: a display shows it only when one of its stations is on it (ADR-045). */
  kdsTab?: KdsTab;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', humanOnly: true },
  { to: '/pos', label: 'POS', permission: 'pos.access' },
  { to: '/orders', label: 'Orders', permission: 'orders.view' },
  { to: '/kitchen', label: 'Kitchen', permission: 'kds.access', kdsTab: 'KITCHEN' },
  { to: '/bar', label: 'Bar', permission: 'kds.access', kdsTab: 'BAR' },
  { to: '/expo', label: 'Expo', permission: 'expo.access' },
  { to: '/tables', label: 'Tables', permission: 'table.view' },
  { to: '/catalog', label: 'Catalog', permission: 'catalog.view' },
  { to: '/employees', label: 'Employees', permission: 'employee.view' },
  { to: '/devices', label: 'Devices', permission: 'device.view' },
  { to: '/shifts', label: 'Shifts', permission: 'shift.review', humanOnly: true },
  { to: '/approvals', label: 'Approvals', permission: 'approval.remote', humanOnly: true },
  { to: '/reports', label: 'Reports', permission: 'report.money', humanOnly: true },
  { to: '/study-pods', label: 'Study Pods', humanOnly: true, later: true },
  { to: '/settings', label: 'Settings', permission: 'settings.view' },
];

export function Shell() {
  const s = useSession();
  const nav = useNavigate();
  const { toast } = useToast();
  const sync = useSyncStatus();
  const device = useDeviceStore();
  const { pathname } = useLocation();
  const theme = useThemePreference();

  const isDevice = s.me?.principal === 'device';
  const terminal = s.me?.terminal ?? null;
  // The counter POS gets the whole screen: the side navigation folds into a slim top bar and the screens
  // are one tap away under Menu (CSS, landscape only). The menu is open for the page it was opened on.
  const onPos = pathname === '/pos' || pathname.startsWith('/pos/');
  const [menuOpenOn, setMenuOpenOn] = useState<string | null>(null);
  const menuOpen = onPos && menuOpenOn === pathname;
  const navId = useId();
  const barRef = useRef<HTMLElement>(null);
  // the POS's page title shows in the bar (PosPage puts it here), so the POS has no title row of its own
  const [pageSlot, setPageSlot] = useState<HTMLElement | null>(null);
  // A tap anywhere but on Menu and its list closes the open menu (the tap itself still lands). Escape anywhere
  // in the bar closes it too and gives the focus back to Menu: a listener on the bar itself, so the POS's
  // title shown there (React portal content, whose events do not reach the bar through React) counts too.
  useEffect(() => {
    if (!menuOpen) return;
    const bar = barRef.current;
    const close = (e: PointerEvent) => {
      const inMenu = [bar?.querySelector('.shell__menu'), bar?.querySelector('nav')].some((el) =>
        el?.contains(e.target as Node),
      );
      if (!inMenu) setMenuOpenOn(null);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpenOn(null);
      bar?.querySelector<HTMLElement>('.shell__menu')?.focus();
    };
    document.addEventListener('pointerdown', close);
    bar?.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      bar?.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);

  const lock = useCallback(
    async (reason: 'manual' | 'idle' = 'manual') => {
      try {
        await closeTerminalSession(reason);
      } catch {
        /* offline: the server will expire it on its own */
      }
      device.setSession(null, null, null);
      await s.refresh();
      nav('/lock', { replace: true });
      if (reason === 'idle') toast('Locked after inactivity');
    },
    [device, nav, s, toast],
  );

  useIdleLock(
    isDevice && s.me?.terminal_session ? (terminal?.idle_lock_seconds ?? null) : null,
    () => void lock('idle'),
  );

  // a Kitchen / Bar display shows the tabs of its own stations; people and other devices keep every tab
  const displayTabs = isDevice ? kdsTabsFor(terminal) : null;
  const visible = NAV.filter((n) => {
    if (n.humanOnly && s.me?.principal !== 'human') return false;
    if (n.permission && !s.can(n.permission)) return false;
    if (n.kdsTab && displayTabs && !displayTabs.includes(n.kdsTab)) return false;
    return true;
  });

  return (
    <div
      className="shell"
      data-principal={s.me?.principal ?? 'none'}
      data-surface={onPos ? 'pos' : undefined}
      data-menu={menuOpen ? 'open' : undefined}
    >
      <aside className="shell__nav" data-depth="3" ref={barRef}>
        {onPos && (
          <Button
            variant="ghost"
            className="shell__menu"
            aria-expanded={menuOpen}
            aria-controls={navId}
            onClick={() => setMenuOpenOn(menuOpen ? null : pathname)}
            data-testid="shell-menu"
          >
            Menu
          </Button>
        )}
        <div className="shell__brand">
          <span className="shell__logo">didto.</span>
          <span className="shell__product">OS</span>
        </div>
        <nav id={navId} aria-label="Main">
          {visible.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => `shell__link${isActive ? ' shell__link--active' : ''}`}
              onClick={() => setMenuOpenOn(null)}
            >
              <span>{n.label}</span>
              {n.later && <span className="shell__later">later</span>}
              {n.to === '/shifts' && <ReviewNavBadge />}
              {n.to === '/approvals' && <ApprovalsNavBadge />}
            </NavLink>
          ))}
        </nav>
        {onPos && <div className="shell__page" ref={setPageSlot} />}
        <div className="shell__foot">
          <SyncPill state={sync.state} pending={sync.pending} />
          {isDevice ? (
            <div className="shell__who">
              <div className="d-row__title">
                {s.me?.terminal_session?.employee_display_name ?? 'Locked'}
              </div>
              <div className="d-row__meta d-mono">
                {terminal?.code} · {terminal?.name}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void lock('manual')}
                data-testid="lock-button"
              >
                Lock
              </Button>
            </div>
          ) : (
            <div className="shell__who">
              <div className="d-row__title" data-testid="me-name">
                {s.me?.employee?.display_name ?? '—'}
              </div>
              <div className="d-row__meta">{displayBusinessName(s.me?.business?.name)}</div>
              <div className="d-inline">
                {/* every login can change how this device looks, whatever its role (Settings,
                    Appearance has the full control) */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setThemePreference(NEXT_THEME[theme])}
                  aria-label={`Theme: ${THEME_LABEL[theme]}. Change to ${THEME_LABEL[NEXT_THEME[theme]]}`}
                  data-testid="theme-switch"
                >
                  Theme: {THEME_LABEL[theme]}
                </Button>
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={() => void s.signOut().then(() => nav('/login'))}
                  data-testid="sign-out"
                >
                  Sign out
                </Button>
              </div>
            </div>
          )}
        </div>
      </aside>
      <main className="shell__main" data-depth={depthForPath(pathname)}>
        <ConnectionBanner />
        <ShellBarContext.Provider value={onPos ? pageSlot : null}>
          <Outlet />
        </ShellBarContext.Provider>
      </main>
    </div>
  );
}

/** The owner's open items, on the Shifts entry (ADR-026). */
function ReviewNavBadge() {
  const s = useSession();
  const locationId = s.me?.active_location_id ?? s.me?.locations[0]?.id ?? null;
  const counts = useReviewCounts(locationId, s.me?.principal === 'human' && s.can('shift.review'));
  const n = counts.data?.red_open ?? 0;
  if (!n) return null;
  return (
    <Badge tone="danger" dot>
      <span data-testid="nav-review-count">{n}</span>
    </Badge>
  );
}

/** What waits on the owner's phone (ADR-033): read only with an authenticator verified in this session. */
function ApprovalsNavBadge() {
  const s = useSession();
  const enabled =
    s.me?.principal === 'human' &&
    s.can('approval.remote') &&
    s.me?.mfa_enrolled === true &&
    s.me?.aal === 'aal2';
  const q = useQuery({
    queryKey: ['remote-approvals'],
    enabled,
    queryFn: fetchRemoteApprovals,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
  const n = q.data?.waiting.length ?? 0;
  if (!n) return null;
  return (
    <Badge tone="warning" dot>
      <span data-testid="nav-approvals-count">{n}</span>
    </Badge>
  );
}

export { SyncPill };
